import { buildImgSrc } from '@/helpers/image-url'
import { useIntl } from '@/intl'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { createPDPClickEvent } from '@/scopes/products/infrastructure/analytics'
import { Button, Icon, Image, LazyLoader, tk, Typography } from '@sunwebgroup/ui-components'
import { styled } from 'styled-components'

export interface DestinationMoreInfoProps {
  moreInfo: {
    sectionTitle?: string
    imageUrl?: string
    regionName?: string
    destinationHref?: string
  }
}

const Container = styled.div`
  position: relative;
  height: 100%;
`

const InnerContainer = styled.div`
  display: flex;
  position: absolute;
  inset: 0;
  align-items: center;
  justify-content: center;
`

const LazyLoaderWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: grid;
`

const StyledImage = styled(Image.Root)`
  object-fit: cover;
  height: 100%;
  width: 100%;
  display: block;
  border-radius: ${tk('radius_large')};
`

const StyledButton = styled(Button)`
  text-align: center;
`

export default function DestinationMoreInfo({ moreInfo }: DestinationMoreInfoProps) {
  const intl = useIntl()
  const noContentFallbackText = intl.formatMessage({ id: 'no_image' })
  const ctaText = intl.formatMessage({ id: 'destination_section_cta' })
  const buttonText = `${ctaText} ${moreInfo.regionName}`
  const { sendEvent } = useAnalytics()

  const imageDestination = moreInfo?.imageUrl ? moreInfo.imageUrl : ''

  const handleClick = () => {
    sendEvent(
      createPDPClickEvent({
        element: 'Button',
        description: 'Destination More Information button clicked',
        result: `Destination More Information button clicked to navigate to ${moreInfo.regionName}`,
      }),
    )
  }

  return (
    <Container>
      <LazyLoader>
        {({ inView }) => (
          <LazyLoaderWrapper>
            <StyledImage
              defaultSource={{ src: buildImgSrc(imageDestination, 760, 160), width: 760, height: 160 }}
              loaderSource={{ src: buildImgSrc(imageDestination, 20) }}
              sources={[
                {
                  src: buildImgSrc(imageDestination, 900, 280),
                  width: 900,
                  height: 280,
                  breakpoint: 'breakpoint_desktop_sm',
                },
              ]}
              alternativeText={buttonText}
              startImmediateLoad={inView}
            >
              <Image.Error>
                <Icon glyph="IconNoImage" size="lg" />
                <Typography $variant="body-lg-bold">{noContentFallbackText}</Typography>
              </Image.Error>
            </StyledImage>
          </LazyLoaderWrapper>
        )}
      </LazyLoader>

      <InnerContainer>
        <StyledButton
          as="a"
          $hierarchy="secondary"
          href={moreInfo?.destinationHref}
          target="_blank"
          onClick={handleClick}
        >
          {buttonText}
        </StyledButton>
      </InnerContainer>
    </Container>
  )
}
